import 'react-slideshow-image/dist/styles.css';
import React, { useState, useRef, useEffect } from 'react';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import { Paper } from '@mui/material';
import { Slide } from 'react-slideshow-image';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { darkModeBackgroudInputs } from '../../../../utils/constants';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { TicketImageInterface } from '../../../../models/TicketModule/TicketImage';

interface SliderProps {
    initialIndex: number;
    photos: TicketImageInterface[];
}

const Slider = ({ photos, initialIndex }: SliderProps) => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [zoom, setZoom] = useState<number>(1);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(initialIndex);
    const [startCoords, setStartCoords] = useState<{ x: number; y: number } | null>(null);
    const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleZoomIn = () => setZoom((prevZoom) => Math.min(prevZoom + 0.2, 3));

    const handleZoomOut = () => {
        setZoom((prevZoom) => {
            const newZoom = Math.max(prevZoom - 0.2, 1);
            if (newZoom === 1) setOffset({ x: 0, y: 0 });
            return newZoom;
        });
    };

    const handleDownload = () => {
        const activePhoto = photos[activeIndex];
        if (activePhoto) {
            const link = document.createElement('a');
            link.href = activePhoto.imageUrl;
            link.download = `photo-${activeIndex + 1}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            console.error('No active photo found for download.');
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom > 1) {
            e.preventDefault();
            setIsDragging(true);
            setStartCoords({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && startCoords) {
            const dx = (e.clientX - startCoords.x) * 0.1;
            const dy = (e.clientY - startCoords.y) * 0.1;

            setOffset((prevOffset) => ({
                x: prevOffset.x + dx,
                y: prevOffset.y + dy,
            }));
            setStartCoords({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setStartCoords(null);
    };

    const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
        const zoomChange = e.deltaY < 0 ? 0.2 : -0.2;
        setZoom((prevZoom) => {
            const newZoom = prevZoom + zoomChange;
            if (newZoom <= 1) setOffset({ x: 0, y: 0 });
            return Math.min(Math.max(newZoom, 1), 3);
        });
    };

    const handleSlideChange = (from: number, to: number) => {
        setActiveIndex(to);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    }

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) container.addEventListener('wheel', handleWheel);
        return () => { if (container) container.removeEventListener('wheel', handleWheel); }
    }, []);

    return (
        <div ref={containerRef} style={{ overflow: 'hidden', position: 'relative' }}>
            <div className='d-flex align-items-center'>
                <span className={`${darkMode ? 'text-white' : 'text-muted'}`}>
                    {activeIndex + 1} / {photos.length}
                </span>
                <span
                    role='button'
                    title={language['ZoomIn']}
                    onClick={handleZoomIn}
                    className={`ms-auto ${darkMode && 'text-white'}`}
                    style={{ marginRight: '10px' }}
                >
                    <ZoomInIcon />
                </span>
                <span
                    role='button'
                    title={language['ZoomOut']}
                    onClick={handleZoomOut}
                    className={`${darkMode && 'text-white'}`}
                    style={{ marginRight: '10px' }}
                >
                    <ZoomOutIcon />
                </span>
                <span
                    role='button'
                    title={language['Download']}
                    className={`${darkMode && 'text-white'}`}
                    onClick={handleDownload}
                >
                    <DownloadIcon />
                </span>
            </div>
            <hr className={`${darkMode && 'text-white opacity-100'}`} />

            <Slide
                key={activeIndex}
                autoplay={false}
                transitionDuration={300}
                defaultIndex={activeIndex}
                onChange={(from, to) => handleSlideChange(from, to)}
            >
                {photos.map((photo, index) => (
                    <Paper
                        key={index}
                        style={{
                            width: '100%',
                            height: '50vh',
                            maxHeight: '100%',
                            overflow: 'hidden',
                            background: darkMode ? darkModeBackgroudInputs : '#F3F3F7',
                        }}
                    >
                        <img
                            src={photo.imageUrl}
                            alt={`Slide ${index}`}
                            ref={imgRef}
                            onMouseDown={handleMouseDown}
                            style={{
                                width: `${100 * zoom}%`,
                                height: `${100 * zoom}%`,
                                objectFit: 'contain',
                                cursor: zoom > 1 ? 'move' : 'default', // Change cursor style based on zoom
                                transition: 'transform 0.3s ease-in-out',
                                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                            }}
                        />
                    </Paper>
                ))}
            </Slide>
        </div>
    );
};

export default Slider;
